import { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, ExternalLink } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import AdminSectionLayout from "../components/admin/AdminSectionLayout";

const API = process.env.REACT_APP_BACKEND_URL;

function display(value) {
  if (value === null || value === undefined || value === "") return "Not recorded";
  return String(value);
}

function formatTime(value) {
  if (!value) return "Not recorded";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

export default function AdminSystem() {
  const [searchParams] = useSearchParams();
  const repairRef = searchParams.get("repair_ref");
  const [incident, setIncident] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!repairRef) {
      setIncident(null);
      setError("");
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setIncident(null);
    setError("");
    axios.get(
      API + "/api/admin/system-repair-incidents/" + encodeURIComponent(repairRef),
      { withCredentials: true },
    ).then((response) => {
      if (!cancelled) setIncident(response.data);
    }).catch((requestError) => {
      if (!cancelled) {
        setError(requestError?.response?.status === 404
          ? "This repair incident could not be found."
          : "Couldn't load the repair incident.");
      }
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [repairRef]);

  return (
    <AdminSectionLayout
      title={repairRef ? "System repair incident" : "System"}
      subtitle={repairRef ? "Captured platform failure · admin detail" : "Env · email · upload limits · logs · diagnostics"}
      icon={repairRef ? AlertTriangle : ExternalLink}
      accent="rose"
      testid="admin-system-page"
    >
      {repairRef && (
        <section data-testid="system-repair-incident-detail" className="rounded-2xl bg-white border border-slate-200 p-5 space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">Incident reference</p>
              <p className="font-mono text-sm font-bold text-slate-900 mt-1">{display(incident?.incident_ref || repairRef)}</p>
            </div>
            {incident?.status && (
              <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-800">
                {incident.status}
              </span>
            )}
          </div>

          {loading && <p role="status" className="text-sm text-slate-500">Loading incident details…</p>}
          {error && <p role="alert" className="text-sm text-rose-700">{error}</p>}
          {incident && (
            <>
              <dl className="grid gap-3 sm:grid-cols-2">
                {[
                  ["Failed specialist", incident.specialist_id],
                  ["Journey / session", incident.session_id],
                  ["Boundary", incident.boundary],
                  ["Failure type", incident.failure_class],
                  ["Occurrences", incident.occurrence_count],
                  ["Recoverable", incident.auto_recoverable === true ? "Yes" : incident.auto_recoverable === false ? "No" : "Not recorded"],
                  ["First seen", formatTime(incident.first_seen_at)],
                  ["Last seen", formatTime(incident.last_seen_at)],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-xl border border-slate-200 p-3">
                    <dt className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</dt>
                    <dd className="mt-1 text-sm text-slate-900 break-words">{display(value)}</dd>
                  </div>
                ))}
              </dl>
              <div className="rounded-xl border border-slate-200 p-3">
                <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Failure detail</p>
                <p className="mt-1 text-sm text-slate-800 whitespace-pre-wrap break-words">{display(incident.failure_detail)}</p>
              </div>
              <p className="text-xs text-slate-500">
                This view does not retry the action or change the incident status.
              </p>
            </>
          )}
          <Link to="/admin/needs-attention" className="inline-flex items-center gap-2 text-sm font-bold text-rose-600 hover:text-rose-700">
            ← Back to Needs Attention
          </Link>
        </section>
      )}

      {!repairRef && (
        <div
          data-testid="admin-system-empty-state"
          className="rounded-2xl bg-white border border-slate-200 p-5"
        >
          <p className="text-sm font-extrabold text-slate-900 leading-tight">
            System tools are in the legacy panel
          </p>
          <p className="text-xs text-slate-500 mt-1.5 leading-snug">
            Diagnostics, email sending state, upload limits and request logs are
            still surfaced from the legacy admin while we migrate them onto their
            own dedicated pages here.
          </p>
          <Link
            to="/admin/legacy"
            data-testid="admin-system-legacy-link"
            className="mt-3 inline-flex items-center gap-2 text-sm font-bold text-rose-600 hover:text-rose-700"
          >
            <ExternalLink className="w-4 h-4" />
            Open legacy admin
          </Link>
        </div>
      )}
    </AdminSectionLayout>
  );
}
